import { z } from "zod";

export const PAGE_CONFIG_SCHEMA = z.object({
  darkMode: z.boolean().optional(),
  simplifiedHeader: z.boolean().optional(),
  hideNavigation: z.boolean().optional(),
  hideBreadcrumbs: z.boolean().optional(),
  hideBreadcrumbsOnFooter: z.boolean().optional(),
  hideBreadcrumbsOnMobile: z.boolean().optional(),
  useHeadingTagForBreadcrumbs: z.boolean().optional(),
  overlayHeader: z.boolean().optional(),
  navigationThemeOverride: z.boolean().optional(),
  transparentHeaderTheme: z.enum(["light", "dark", "inherit"]).optional(),
  analytics: z.boolean().default(true).optional(),
  pageName: z.string().default("none").optional(),
  pageLoadTracking: z.enum(["none", "page_view"]).default("none").optional(),
  withoutHeader: z.boolean().optional(),
  withoutFooter: z.boolean().optional(),
  isPageShouldBeProtected: z.boolean().optional(),
});

export type PageConfigSchema = z.infer<typeof PAGE_CONFIG_SCHEMA>;

export type PageConfig = NonNullable<Required<PageConfigSchema>>;

const PAGE_CONFIG_FIELD_KEYS = Object.keys(PAGE_CONFIG_SCHEMA.shape) as (keyof PageConfig)[];

export const DEFAULT_PAGE_CONFIG_VALUE: PageConfig = {
  darkMode: false,
  hideNavigation: false,
  simplifiedHeader: false,
  hideBreadcrumbs: false,
  hideBreadcrumbsOnFooter: false,
  hideBreadcrumbsOnMobile: false,
  useHeadingTagForBreadcrumbs: false,
  overlayHeader: false,
  analytics: false,
  pageName: "none",
  pageLoadTracking: "none",
  navigationThemeOverride: false,
  transparentHeaderTheme: "inherit",
  withoutHeader: false,
  withoutFooter: false,
  isPageShouldBeProtected: false,
};

const hasPageConfigProperties = (value: unknown): value is PageConfigSchema => {
  return PAGE_CONFIG_SCHEMA.safeParse(value).success;
};

export const getPageConfigProperties = (value: unknown): PageConfig => {
  if (!hasPageConfigProperties(value)) {
    return DEFAULT_PAGE_CONFIG_VALUE;
  }

  const {
    darkMode = false,
    hideNavigation = false,
    simplifiedHeader = false,
    hideBreadcrumbs = false,
    hideBreadcrumbsOnFooter = false,
    useHeadingTagForBreadcrumbs = false,
    hideBreadcrumbsOnMobile = false,
    overlayHeader = false,
    navigationThemeOverride = false,
    transparentHeaderTheme = "inherit",
    analytics = false,
    pageName = "none",
    pageLoadTracking = "none",
    withoutHeader = false,
    withoutFooter = false,
    isPageShouldBeProtected = false,
  } = value;

  return {
    darkMode,
    hideNavigation,
    simplifiedHeader,
    hideBreadcrumbs,
    hideBreadcrumbsOnFooter,
    hideBreadcrumbsOnMobile,
    useHeadingTagForBreadcrumbs,
    overlayHeader,
    navigationThemeOverride,
    transparentHeaderTheme,
    analytics,
    pageName,
    pageLoadTracking,
    withoutHeader,
    withoutFooter,
    isPageShouldBeProtected,
  };
};

const setPageConfigValue = <K extends keyof PageConfig>(merged: PageConfig, key: K, value: PageConfig[K]): void => {
  merged[key] = value;
};

export function mergePageConfigWithLiveBuilderData(base: PageConfig, contentData: unknown): PageConfig {
  if (!contentData || typeof contentData !== "object") {
    return base;
  }

  if (!PAGE_CONFIG_FIELD_KEYS.some((key) => key in contentData)) {
    return base;
  }

  const parsed = getPageConfigProperties(contentData);
  const merged = { ...base };

  for (const key of PAGE_CONFIG_FIELD_KEYS) {
    if (key in contentData) {
      setPageConfigValue(merged, key, parsed[key]);
    }
  }

  return merged;
}
